import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer';

import {
  Message,
  User,
  ConversationWithRelations,
  MessagePartial,
} from '../ellipsisApi';
import { enhancedApi as api } from '../enhancedApi';
import { RootState } from '../store';

export const postReadReceipt = createAsyncThunk(
  'chat/readReceipt',
  async (payload: { message: Message; user: User }, { dispatch }) => {
    if (payload) {
      const currentReceipts = payload.message.readReceipts ?? [];
      const newReceipts = [
        ...currentReceipts,
        { userId: payload.user.id ?? '', readDate: new Date().toISOString() },
      ];
      const messagePartial: MessagePartial = {
        conversationId: payload.message.conversationId,
        readReceipts: newReceipts,
      };
      dispatch(
        api.endpoints.patchMessagesById.initiate({
          id: payload.message.id!,
          messagePartial,
        })
      );
      return { ...payload.message, readReceipts: newReceipts } as Message;
    }
  }
);

export const postMessage = createAsyncThunk(
  'chat/sendMessage',
  async (payload: Message, { dispatch }) => {
    if (payload) {
      const newMessage = {
        ...payload,
        readReceipts: [
          {
            userId: payload.authoredByUserId ?? '',
            readDate: new Date().toISOString(),
          },
        ],
      };
      const result = dispatch(
        api.endpoints.postMessages.initiate({ newMessage })
      );
      const response = await result;
      return response.data;
    }
  }
);

interface ChatState {
  selectedConversation: ConversationWithRelations | null;
  messageModalOpen: boolean;
}

const initialState: ChatState = {
  selectedConversation: null,
  messageModalOpen: false,
};

function getUpdatedConversation(
  state: WritableDraft<ChatState>,
  payload: Message | undefined
): ConversationWithRelations | undefined {
  if (!payload || !state.selectedConversation) return;

  const conversation = state.selectedConversation;

  if (conversation.id === payload.conversationId) {
    const messages = conversation.messages ? [...conversation.messages] : [];
    const existingIndex = messages.findIndex((m) => m.id === payload.id);

    if (existingIndex === -1) {
      messages.push(payload);
    } else {
      messages.splice(existingIndex, 1, payload);
    }

    return {
      ...conversation,
      messages,
    };
  }

  return conversation;
}

const chatSlice = createSlice({
  name: 'chat',
  initialState: initialState,
  reducers: {
    openMessageModal(state) {
      state.messageModalOpen = true;
    },
    closeMessageModal(state) {
      state.messageModalOpen = false;
    },
    setSelectedConversation(
      state,
      action: PayloadAction<ConversationWithRelations | null>
    ) {
      state.selectedConversation = action.payload;
    },
    updateMessageStatus(state, action: PayloadAction<Message>) {
      if (
        state.selectedConversation &&
        state.selectedConversation.id === action.payload.conversationId
      ) {
        const newConversation = getUpdatedConversation(state, action.payload);
        if (newConversation) {
          state.selectedConversation =
            newConversation as WritableDraft<ConversationWithRelations>;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postReadReceipt.fulfilled, (state, { payload }) => {
      const newConversation = getUpdatedConversation(state, payload);
      state.selectedConversation =
        newConversation as WritableDraft<ConversationWithRelations>;
    });
    builder.addCase(postMessage.fulfilled, (state, { payload }) => {
      const newConversation = getUpdatedConversation(state, payload);
      state.selectedConversation =
        newConversation as WritableDraft<ConversationWithRelations>;
    });
  },
});

export const newSelectedConversation = (state: RootState) =>
  state.chat.selectedConversation;

export const {
  openMessageModal,
  closeMessageModal,
  setSelectedConversation,
  updateMessageStatus,
} = chatSlice.actions;

export default chatSlice;
